import React, { useState, useEffect } from "react";
import { Form, Button, Dropdown } from "semantic-ui-react";
import { useFormik } from "formik";
import { QuizApi } from "../../../../api/quiz";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./QuizForm.form";
import "./QuizForm.scss";

export function QuizForm(props) {
  const { onClose, onReload, quiz } = props;
  const { accessToken, user } = useAuth();
  const quizController = new QuizApi();

  // ==============================
  // 1) FORMIK: maneja name y description
  // ==============================
  const formik = useFormik({
    initialValues: initialValues(quiz),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (values) => {
      try {
        // 1. Guarda (por si acaso) la pregunta actual en edición
        storePartialQuestion();

        // 2. Filtra las preguntas válidas (completas)
        const validQuestions = filterOutIncomplete(questionsArray);

        // 3. Si no hay preguntas válidas, puedes decidir si permitir o bloquear
        if (validQuestions.length === 0) {
          if (!window.confirm("No hay preguntas completas. ¿Deseas enviar sin preguntas?")) {
            return;
          }
        }

        // 4. Ensambla los datos finales
        const finalData = {
          name: values.name,
          description: values.description,
          // Convierte correctIndex a la respuesta real
          questions: validQuestions.map((q) => ({
            question: q.question,
            responses: q.responses,
            correct: q.responses[q.correctIndex],
          })),
        };

        // 5. Petición al backend (create o update)
        if (quiz) {
          await quizController.updateQuiz(accessToken, quiz._id, finalData);
        } else {
          await quizController.createQuiz(accessToken, user.user.teacher, finalData);
        }

        onReload();
        onClose();
      } catch (error) {
        console.error("Error al enviar el formulario de quizz: ", error);
      }
    },
  });

  // ==============================
  // 2) Estado para el array de preguntas y la pregunta actual
  // ==============================
  const [questionsArray, setQuestionsArray] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // questionLocal: la pregunta que se está editando (enunciado, 4 respuestas, índice de la correcta)
  const [questionLocal, setQuestionLocal] = useState({
    question: "",
    responses: ["", "", "", ""],
    correctIndex: -1,
  });

  // Al entrar en modo edición, adaptamos quiz.questions al formato local
  useEffect(() => {
    if (quiz && quiz.questions && quiz.questions.length > 0) {
      const adapted = quiz.questions.map((q) => {
        const idx = q.responses.findIndex((resp) => resp === q.correct);
        return {
          question: q.question,
          responses: [...q.responses],
          correctIndex: idx >= 0 ? idx : -1,
        };
      });
      setQuestionsArray(adapted);
    }
  }, [quiz]);

  // Cada vez que cambie currentQuestionIndex, cargamos la data en questionLocal
  useEffect(() => {
    if (questionsArray[currentQuestionIndex]) {
      setQuestionLocal({ ...questionsArray[currentQuestionIndex] });
    } else {
      // Nueva pregunta vacía
      setQuestionLocal({
        question: "",
        responses: ["", "", "", ""],
        correctIndex: -1,
      });
    }
  }, [currentQuestionIndex, questionsArray]);

  // ==============================
  // 3) Manejo de name/description (Formik)
  // ==============================
  const handleNameDescriptionChange = (e) => {
    formik.handleChange(e);
  };

  // ==============================
  // 4) Manejo de questionLocal
  // ==============================
  const handleQuestionChange = (e) => {
    setQuestionLocal({ ...questionLocal, question: e.target.value });
  };

  const handleResponseChange = (index, value) => {
    const updated = [...questionLocal.responses];
    updated[index] = value;
    setQuestionLocal({ ...questionLocal, responses: updated });
  };

  const handleCorrectChange = (e, { value }) => {
    setQuestionLocal({ ...questionLocal, correctIndex: value });
  };

  // Opciones del dropdown: el value es el índice (0..3)
  const correctOptions = questionLocal.responses.map((resp, i) => ({
    key: i,
    text: resp || `Opción ${i + 1}`,
    value: i,
  }));

  // ==============================
  // 5) Al cambiar de pregunta, guardamos parciales sin validación
  // ==============================
  const storePartialQuestion = () => {
    // Si la pregunta está completamente vacía, no guardar nada
    const isEmptyQuestion =
      (!questionLocal.question || questionLocal.question.trim() === "") &&
      questionLocal.responses.every((r) => !r || r.trim() === "") &&
      questionLocal.correctIndex === -1;
      
    if (isEmptyQuestion) return;
  
    // Sino, guarda la pregunta actual en la posición actual
    const updated = [...questionsArray];
    updated[currentQuestionIndex] = {
      question: questionLocal.question,
      responses: [...questionLocal.responses],
      correctIndex: questionLocal.correctIndex,
    };
    setQuestionsArray(updated);
  };

  const handleNextQuestion = () => {
    storePartialQuestion();
    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex <= 0) return;
    storePartialQuestion();
    setCurrentQuestionIndex(currentQuestionIndex - 1);
  };

  // ==============================
  // 6) Finalizar => formik.submit (que hace la filtración)
  // ==============================
  const handleFinish = () => {
    storePartialQuestion();
    formik.handleSubmit();
  };

  // ==============================
  // 7) Filtrar preguntas incompletas
  // ==============================
  const filterOutIncomplete = (questions) => {
    // “Completa” = tiene enunciado, 4 respuestas no vacías y una correctaIndex válido
    return questions.filter((q) => {
      if (!q) return false;
      if (!q.question) return false;
      if (q.responses.length !== 4) return false;
      if (q.responses.some((resp) => !resp)) return false;
      if (q.correctIndex < 0 || q.correctIndex > 3) return false;
      return true;
    });
  };

  return (
    <div className="quiz-form-edit-container">
      {/* Campos name/description con Formik */}
      <Form>
        <Form.Group widths="equal">
          <Form.Input
            name="name"
            label="Nombre de la Evaluación"
            placeholder="Ej: Evaluación de Historia"
            onChange={handleNameDescriptionChange}
            value={formik.values.name}
            error={formik.errors.name}
          />
          <Form.Input
            name="description"
            label="Descripción"
            placeholder="Ej: Preguntas sobre la historia europea"
            onChange={handleNameDescriptionChange}
            value={formik.values.description}
            error={formik.errors.description}
          />
        </Form.Group>
      </Form>

      {/* Navegación de preguntas */}
      <div className="quiz-questions-nav">
        <h4>{`Pregunta #${currentQuestionIndex + 1}`}</h4>
        <Button
          icon="chevron left"
          disabled={currentQuestionIndex <= 0}
          onClick={handlePrevQuestion}
        />
        <Button icon="chevron right" onClick={handleNextQuestion} />
        <h4>{`< Añade nueva pregunta`}</h4>
      </div>

      {/* Formulario de la pregunta actual */}
      <Form>
        <Form.Field>
          <label>Enunciado de la pregunta</label>
          <Form.Input
            placeholder="Ej: ¿Cuál es la capital de Francia?"
            value={questionLocal.question}
            onChange={handleQuestionChange}
          />
        </Form.Field>
        <Form.Field>
          <label>Respuestas</label>
          {questionLocal.responses.map((resp, i) => (
            <Form.Input
              key={i}
              placeholder={`Respuesta ${i + 1}`}
              value={resp}
              onChange={(e) => handleResponseChange(i, e.target.value)}
              style={{ marginBottom: "0.5rem" }}
            />
          ))}
        </Form.Field>
        <Form.Field>
          <label>Respuesta Correcta</label>
          <Dropdown
            fluid
            selection
            options={correctOptions}
            value={
              questionLocal.correctIndex >= 0 ? questionLocal.correctIndex : null
            }
            onChange={handleCorrectChange}
            placeholder="Selecciona la respuesta correcta"
          />
        </Form.Field>
      </Form>

      {/* Botón para finalizar */}
      <div className="quiz-edit-buttons">
        <Button  onClick={handleFinish}>
          Finalizar y Enviar
        </Button>
      </div>
    </div>
  );
}
